import { useAuth } from '@clerk/clerk-react';

import { trpc } from '@/lib/api/trpc';
import { crmV3OriginFor } from '@/hooks/use-crm-v3-redirect';
import { useSessionClaims } from '@/hooks/use-session-claims';

export type CrmV3BannerState = 'hidden' | 'open' | 'join' | 'waiting';

type WaitlistStatus = 'waiting' | 'approved' | 'declined';

type BannerInput = {
  v3Origin: string | null;
  /** False until Clerk has loaded the session claims. */
  isClaimsLoaded: boolean;
  isStaff: boolean;
  /** Clerk "Sign in as user": the claims are the merchant's, the person is staff. */
  isImpersonating: boolean;
  /** `undefined` while `v3.access.status` is pending or failed. */
  hasAccess: boolean | undefined;
  /** `undefined` while `v3.waitlist.get` is pending or failed; `null` = no entry yet. */
  entry: { status: WaitlistStatus } | null | undefined;
};

/**
 * What the CRM V3 banner of V2 shows. Hidden on hosts without a V3 origin (partner hosts, previews,
 * localhost), for Tagada staff (signed in as themselves or as a merchant), while loading, on error, for a declined entry, and for an approved
 * entry when CRM V3 is not open to this person: approval grants the person who joined, not every
 * member of the account, so "Open CRM V3" would land them on the waitlist again.
 */
export function crmV3BannerState(input: BannerInput): CrmV3BannerState {
  if (input.v3Origin === null || !input.isClaimsLoaded || input.isStaff || input.isImpersonating) return 'hidden';
  if (input.hasAccess === undefined) return 'hidden';
  if (input.hasAccess) return 'open';
  if (input.entry === undefined) return 'hidden';
  if (input.entry === null) return 'join';
  return input.entry.status === 'waiting' ? 'waiting' : 'hidden';
}

const QUERY_OPTIONS = { retry: 1, refetchOnWindowFocus: false, staleTime: Infinity } as const;

/**
 * State and link of the CRM V3 banner for the org in the URL. Never joins the waitlist: CRM V3 does.
 * `enabled: false` (closed or snoozed banner) asks nothing: `v3.waitlist.get` can write on read.
 */
export function useCrmV3Banner(
  orgSlug: string,
  { enabled }: { enabled: boolean },
): { state: CrmV3BannerState; href: string | null } {
  const v3Origin = typeof window === 'undefined' ? null : crmV3OriginFor(window.location.hostname);
  const claims = useSessionClaims();
  const { actor } = useAuth();
  const isStaff = claims.isSuperAdmin || claims.isAccountManager;
  const isImpersonating = Boolean(actor);
  const canAsk = enabled && v3Origin !== null && claims.isLoaded && !isStaff && !isImpersonating && orgSlug.length > 0;

  const access = trpc.v3.access.status.useQuery(undefined, { ...QUERY_OPTIONS, enabled: canAsk });
  const hasAccess = access.data?.hasAccess;
  const waitlist = trpc.v3.waitlist.get.useQuery(undefined, { ...QUERY_OPTIONS, enabled: canAsk && hasAccess === false });

  const state = canAsk
    ? crmV3BannerState({ v3Origin, isClaimsLoaded: claims.isLoaded, isStaff, isImpersonating, hasAccess, entry: waitlist.data?.entry })
    : 'hidden';
  const href = v3Origin === null ? null : `${v3Origin}/org/${encodeURIComponent(orgSlug)}`;
  return { state, href };
}
